import { Box, CircularProgress, Stack, TextField, Typography, Grid } from "@mui/material";
import MDBox from "components/MDBox";
import { useRef, useState } from "react";

import MDButton from "components/MDButton";
import { Form, Formik } from "formik";
import { putAPICall } from "lib/callApi";
import { CREATE_KPI_TEMPLATES } from "lib/constants";
import { enqueueSnackbar } from "notistack";
import Swal from "sweetalert2";
import * as Yup from "yup";
import AssignedToUsers from "./AssignedToUsers";
import EditKPITable from "./EditKPITable";

// eslint-disable-next-line func-names
Yup.addMethod(Yup.array, "unique", function (message, mapper = (a) => a) {
  // eslint-disable-next-line react/no-this-in-sfc
  return this.test("unique", message, (list) => list.length === new Set(list.map(mapper)).size);
});
const validationSchema = Yup.object({
  name: Yup.string().required("Please enter name."),
  description: Yup.string().required("Please enter description."),
  assignedUserId: Yup.array().of(
    Yup.object().shape({
      id: Yup.number().required("Please select user").typeError("Please select user"),
      name: Yup.string(),
    })
  ),
  kpiActivities: Yup.array()
    .of(
      Yup.object().shape({
        id: Yup.number().nullable(),
        name: Yup.string().required("Please select KPI").typeError("Please select KPI"),
        points: Yup.number()
          .min(1, "Invalid point")
          .max(999, "Invalid point")
          .typeError("Invalid point"),
        count: Yup.number()
          .min(1, "Invalid kpiNo")
          .max(999, "Invalid kpiNo")
          .typeError("Invalid kpiNo"),
      })
    )
    .unique("New KPI names cannot be same.", (a) => a?.name?.trim())
    .required("Please select KPI Activity"),
});

function EditTemplate({
  getKpiActivities,
  kpiActivities,
  initialValues,
  handleDetailModalClose,
  handleCancel,
  id,
  assignedUsers,
  setAssignedUsers,
  alreadyAssignedUsers,
  oldAssignedUsers,
  assignUserToTemplate,
  isMobileDisplay,
}) {
  const editTemplateFormRef = useRef();
  const KPI_TEMPLATES_STATUS = {
    ACTIVE: "active",
    DRAFTED: "drafted",
    ALYSIO_TEMPLATE: "alysio_template",
  };

  const [isRequestApiLoading, setIsRequestApiLoading] = useState(false);

  const editTemplateApi = async (body, keepUserSettings = false) => {
    const response = await putAPICall(`${CREATE_KPI_TEMPLATES}/${id}`, true, {
      ...body,
      kpiActivities: body.kpiActivities,
      status: body.assignedUserId.length
        ? KPI_TEMPLATES_STATUS.ACTIVE
        : KPI_TEMPLATES_STATUS.DRAFTED,
      keepUserSettings,
    });
    if (response?.data?.success) {
      await assignUserToTemplate(true);
      handleDetailModalClose(undefined, undefined, true);
      enqueueSnackbar(
        JSON.stringify({
          icon: "success",
          title: "Success",
          text: "Template updated successfully!",
        }),
        {
          variant: "custom",
        }
      );

      getKpiActivities();
    } else {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "error",
          text: "Could not update template, Please try again!",
        }),
        {
          variant: "custom",
        }
      );
    }

    setIsRequestApiLoading(false);
  };
  // eslint-disable-next-line consistent-return
  const editTemplate = async (body) => {
    setIsRequestApiLoading(true);
    try {
      const customKpiTemplatesUsedByUsers = body?.assignedUserId?.filter(
        (user) => user?.customUserKpiTemplate
      );
      handleDetailModalClose(undefined, undefined, false);
      return Swal.fire({
        title: `Update template ${body.name}?`,
        ...(customKpiTemplatesUsedByUsers?.length
          ? {
              text: `${customKpiTemplatesUsedByUsers
                ?.map(
                  (customKpiTemplatesUsedByUser, i) =>
                    `${
                      i === customKpiTemplatesUsedByUsers.length - 1 &&
                      customKpiTemplatesUsedByUsers.length > 1
                        ? "and "
                        : ""
                    }${customKpiTemplatesUsedByUser?.name}`
                )
                ?.join(", ")} ${
                customKpiTemplatesUsedByUsers?.length > 1 ? "have" : "has"
              } a customized template`,
              input: "radio",
              inputOptions: {
                keepUserSettings: "Keep customized KPIs",
                updateAll: "Update all assigned",
              },
              inputValue: "updateAll",
            }
          : {}),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes",
        reverseButtons: true,
      }).then(async (result) => {
        if (result.isConfirmed) {
          await editTemplateApi(body, result?.value === "keepUserSettings");
        } else {
          setIsRequestApiLoading(false);
        }
      });
    } catch (error) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "error",
          text: "Could not update template, Please try again!",
        }),
        {
          variant: "custom",
        }
      );
      setIsRequestApiLoading(false);
    }
  };

  const onSaveAssignedUsers = () => editTemplateFormRef?.current?.submitForm();

  return (
    <Box>
      {isRequestApiLoading ? (
        <Stack alignItems="center">
          <CircularProgress
            sx={{
              position: "fixed",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 2,
            }}
            color="info"
            size="10vh"
          />
        </Stack>
      ) : (
        ""
      )}
      <Typography id="modal-modal-title" variant="h5" component="h2">
        Edit KPI Template {initialValues.name}
      </Typography>
      <Typography variant="p" sx={{ fontSize: "14px" }}>
        * Please use the Manage KPIs tab to edit existing KPI names
      </Typography>

      <MDBox>
        <Box
          sx={{
            "& .MuiInputLabel-formControl": { lineHeight: "15px" },
            "& .MuiFormHelperText-root": { color: "#d32f2f" },
          }}
        >
          <Formik
            style={{ marginTop: "2rem" }}
            initialValues={{ ...initialValues }}
            enableReinitialize
            validateOnChange
            validationSchema={validationSchema}
            onSubmit={editTemplate}
            innerRef={editTemplateFormRef}
          >
            {({ errors, values, touched, setValues, setFieldValue }) => (
              <Form>
                <Grid container spacing={1} direction="column" sx={{ mb: "2rem", mt: "1rem" }}>
                  <Grid item>
                    <TextField
                      label="Template Name"
                      variant="standard"
                      defaultValue={values.name}
                      onChange={(event) => setFieldValue("name", event.target.value)}
                      error={Boolean(touched.name && errors.name)}
                      helperText={touched.name && errors.name}
                      sx={{ width: "100%" }}
                      inputProps={{ maxLength: 30 }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item mt="0.5rem">
                    <TextField
                      label="Description"
                      variant="standard"
                      defaultValue={values.description}
                      onChange={(event) => setFieldValue("description", event.target.value)}
                      error={Boolean(touched.description && errors.description)}
                      helperText={touched.description && errors.description}
                      sx={{ width: "100%" }}
                      inputProps={{ maxLength: 255 }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item>
                    <AssignedToUsers
                      assignedUsers={assignedUsers}
                      setAssignedUsers={setAssignedUsers}
                      alreadyAssignedUsers={alreadyAssignedUsers}
                      oldAssignedUsers={oldAssignedUsers}
                      handleDetailModalClose={handleDetailModalClose}
                      isRequestApiLoading={isRequestApiLoading}
                      onSaveAssignedUsers={onSaveAssignedUsers}
                      showButtons={false}
                    />
                  </Grid>
                </Grid>

                <EditKPITable
                  kpiActivities={kpiActivities}
                  setFieldValue={setFieldValue}
                  setValues={setValues}
                  values={values}
                  touched={touched}
                  errors={errors}
                  isMobileDisplay={isMobileDisplay}
                />
                <MDBox mt={4} sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <MDButton onClick={handleCancel} color="gray80" style={{ marginRight: "10px" }}>
                    Cancel
                  </MDButton>
                  <MDButton type="submit" color="primaryRed" disabled={isRequestApiLoading}>
                    {isRequestApiLoading ? "Loading..." : "Save"}
                  </MDButton>
                </MDBox>
              </Form>
            )}
          </Formik>
        </Box>
      </MDBox>
    </Box>
  );
}

export default EditTemplate;
